import { apiFetch } from '../lib/api'

export interface AlphaPickData {
  id?: number;
  company: string;
  symbol: string;
  picked: string | null;
  return_val: string;
  sector: string;
  rating: string;
  holding_percent: string;
  high_52w?: number | null;
  low_52w?: number | null;
  current_price?: number | null;
  created_at?: string;
  updated_at?: string;
}

export class AlphaPickService {
  async saveAlphaPicks(picks: AlphaPickData[]): Promise<void> {
    try {
      const response = await apiFetch('/api/alpha-picks/bulk-upsert', {
        method: 'POST',
        body: JSON.stringify({ picks }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save alpha picks: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Alpha picks saved successfully:', result);
    } catch (error) {
      console.error('Error saving alpha picks:', error);
      throw error;
    }
  }

  async calculateReturns(): Promise<AlphaPickData[]> {
    try {
      const response = await apiFetch('/api/alpha-picks/calculate-returns', {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Failed to calculate returns: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Returns calculated successfully:', result.message);
      return result.data || [];
    } catch (error) {
      console.error('Error calculating returns:', error);
      throw error;
    }
  }

  async getAllAlphaPicks(): Promise<AlphaPickData[]> {
    try {
      const response = await apiFetch('/api/alpha-picks');

      if (!response.ok) {
        throw new Error(`Failed to fetch alpha picks: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error fetching alpha picks:', error);
      throw error;
    }
  }

  transformStockDataToAlphaPicks(stockData: any[]): AlphaPickData[] {
    const picks = stockData
      .map(stock => ({
        company: (stock.company || '').trim(),
        symbol: (stock.symbol || '').trim().toUpperCase(),
        picked: this.parseDateString(stock.picked),
        return_val: (stock.return || '').trim(),
        sector: (stock.sector || '').trim(),
        rating: (stock.rating || '').trim(),
        holding_percent: (stock.holdingPercent || '').trim()
      }))
      .filter(pick =>
        pick.company &&
        pick.symbol &&
        pick.company !== 'Company' && // Filter out header row
        pick.symbol !== 'Symbol'
      );

    // Remove duplicates based on symbol (keep the first occurrence)
    const uniquePicks = picks.filter((pick, index, self) =>
      index === self.findIndex(p => p.symbol === pick.symbol)
    );

    console.log(`Transformed ${uniquePicks.length} unique alpha picks from ${stockData.length} original rows`);
    return uniquePicks;
  }

  private parseDateString(dateStr: string): string | null {
    if (!dateStr || dateStr.trim() === '') return null;

    const cleanDate = dateStr.trim();
    console.log(`Parsing date: "${cleanDate}"`); // Debug log

    // Handle mm/dd/yyyy format (most common in Google Sheets)
    const mmddyyyyMatch = cleanDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mmddyyyyMatch) {
      const [, month, day, year] = mmddyyyyMatch;
      const result = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      console.log(`MM/DD/YYYY format: ${cleanDate} -> ${result}`);
      return result;
    }

    // Handle dd/mm/yyyy format
    const ddmmyyyyMatch = cleanDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (ddmmyyyyMatch) {
      const [, first, second, year] = ddmmyyyyMatch;
      // Assume dd/mm/yyyy if first part > 12
      if (parseInt(first) > 12) {
        const result = `${year}-${second.padStart(2, '0')}-${first.padStart(2, '0')}`;
        console.log(`DD/MM/YYYY format: ${cleanDate} -> ${result}`);
        return result;
      }
    }

    // Handle yyyy-mm-dd format (already correct)
    if (/^\d{4}-\d{2}-\d{2}$/.test(cleanDate)) {
      console.log(`ISO format: ${cleanDate} -> ${cleanDate}`);
      return cleanDate;
    }

    // Handle other common formats like "Month DD, YYYY" or "DD Month YYYY"
    try {
      const date = new Date(cleanDate);
      if (!isNaN(date.getTime())) {
        const result = date.toISOString().split('T')[0];
        console.log(`Date object parse: ${cleanDate} -> ${result}`);
        return result;
      }
    } catch (error) {
      console.warn('Date parsing failed:', error);
    }

    // Handle Google Sheets Date function format: Date(year,month,day)
    const googleDateMatch = cleanDate.match(/Date\((\d{4}),(\d{1,2}),(\d{1,2})\)/);
    if (googleDateMatch) {
      const [, year, month, day] = googleDateMatch;
      const adjustedMonth = parseInt(month) + 1; // Google uses 0-indexed months
      const result = `${year}-${adjustedMonth.toString().padStart(2, '0')}-${day.padStart(2, '0')}`;
      console.log(`Google Date format: ${cleanDate} -> ${result}`);
      return result;
    }

    console.warn(`Could not parse date: "${cleanDate}"`);
    return null;
  }
}
